import customersCRM from '../data/customersCRM';
import { authenticatedFetch } from '../utils/apiInterceptor';
import { resilientFetch, TIMEOUT_NORMAL, TIMEOUT_SLOW } from '../utils/resilientFetch';
import { classifyError } from '../utils/errorTaxonomy';

/**
 * The transport for the raw calls in this module.
 *
 * These were bare `fetch`, so a request the server never answered — which is
 * every request for the 30-120s a deploy takes — left the caller waiting with no
 * deadline, and the browser's own `TypeError: Failed to fetch` reached the UI
 * verbatim. This is where the Orders list gets its data, so that string was the
 * one customers actually saw.
 *
 * Reads retry with jittered backoff; writes get a deadline but are not replayed,
 * since neither a timeout nor a 502 proves the server rejected them.
 *
 * `fetchImpl` is plain `fetch` because these call sites build their own
 * Authorization header. Routing them through `authenticatedFetch` as well would
 * put two refresh paths on one request; that is a separate change.
 */
const apiFetch = (url, options = {}, resilience = {}) =>
  resilientFetch(url, options, {
    timeoutMs: TIMEOUT_NORMAL,
    fetchImpl: (u, o) => fetch(u, o),
    ...resilience,
  });

/** For endpoints that build a file or call an upstream inline. */
const apiFetchSlow = (url, options = {}) =>
  apiFetch(url, options, { timeoutMs: TIMEOUT_SLOW, retries: 0 });

/**
 * Attaches a description of the failure without altering the error itself.
 *
 * An earlier version also overwrote `error.message` with the friendly copy, so
 * that the many callers rendering `err.message` would improve for free. They did
 * not: roughly forty call sites do things like
 * `alert('Error updating order status: ' + error.message)`, so a write that
 * failed for an unrelated reason began announcing high traffic — wrong, and it
 * hid the real cause. That reached production.
 *
 * `message` is therefore left untouched. Callers that want the friendly copy
 * read `error.classified.message` deliberately.
 */
function describeFailure(error) {
  if (!error) return error;
  // A cancellation is not a failure and must never be rendered.
  if (error.aborted || error.name === 'RequestAbortedError') return error;
  if (error.classified) return error;

  error.classified = classifyError(error);
  return error;
}

/**
 * The error for a non-ok response, carrying the status rather than only a
 * string, so callers can tell 404 from 502 without matching on text.
 */
function statusError(response) {
  const error = new Error(`HTTP ${response.status}: ${response.statusText || 'Request failed'}`);
  error.status = response.status;
  error.classified = classifyError(error, { response });
  return error;
}

// Dummy API utility for demonstration
const calendarEvents = [
  { date: '2025-06-03', brand: 'Brand A', event: 'Product Launch' },
  { date: '2025-06-05', brand: 'Brand B', event: 'Webinar' },
  { date: '2025-06-05', brand: 'Brand C', event: 'Social Media Campaign' },
  { date: '2025-06-12', brand: 'Brand D', event: 'Press Release' },
  { date: '2025-06-12', brand: 'Brand A', event: 'Influencer Collaboration' },
  { date: '2025-06-15', brand: 'Brand E', event: 'Charity Event' },
  { date: '2025-06-20', brand: 'Brand F', event: 'Pop-up Shop' },
  { date: '2025-06-20', brand: 'Brand G', event: 'Blog Post' },
  { date: '2025-06-20', brand: 'Brand H', event: 'Investor Meeting' },
  { date: '2025-06-20', brand: 'Brand I', event: 'Community Outreach' },
  { date: '2025-06-20', brand: 'Brand J', event: 'Email Newsletter' },
];

export async function apiRequest({ method, url, data, retries = 3, retryDelay = 1000 }) {
  console.log(`apiRequest called: ${method} ${url}`);


  // Handle offline CRM endpoints with real HTTP requests that fall back to mock data
  if (url.startsWith('/api/core/offline-crm/')) {
    const mockBaseUrl = process.env.REACT_APP_API_BASE_URL;
    const fullUrl = `${mockBaseUrl}${url}`;


    try {
      // Use authenticated fetch wrapper which handles token refresh automatically
      const response = await authenticatedFetch(fullUrl, {
        method: method || 'GET',
        body: data ? JSON.stringify(data) : undefined,
      });


      const result = await response.json();
      console.log('Real API response received:', result);
      return result;


    } catch (error) {
      console.warn('Real API request failed, falling back to mock data:', error.message);

      // Simulate network delay for realistic UX even with mock data
      await new Promise(resolve => setTimeout(resolve, 300 + Math.random() * 500));


      // Return mock data based on URL with pagination support
      if (url.includes('/api/core/offline-crm/customers/')) {
        // Parse query parameters for pagination and filtering
        const urlObj = new URL(fullUrl);
        const page = parseInt(urlObj.searchParams.get('page')) || 1;
        const limit = parseInt(urlObj.searchParams.get('limit')) || 10;
        const search = urlObj.searchParams.get('search') || '';
        const status = urlObj.searchParams.get('status') || '';
        const business_type = urlObj.searchParams.get('business_type') || '';

        // Extended mock customers data
        const allCustomers = [
          {
            id: 1,
            company_name: "TechCorp Solutions",
            business_type: "distributor",
            industry: "Technology",
            annual_revenue: "5000000.00",
            employee_count: 250,
            status: {
              id: 2,
              name: "Active",
              color_code: "#28a745"
            },
            credit_limit: "100000.00",
            payment_terms: "net_30",
            address: "123 Tech Street, Silicon Valley, CA 94000",
            phone: "+1-555-TECH-001",
            email: "contact@techcorp.com",
            website: "https://techcorp.com",
            registration_date: "2024-01-15",
            last_order_date: "2024-06-20",
            total_orders: 3,
            total_order_value: "275000.00",
            primary_contacts: [
              {
                id: 1,
                first_name: "John",
                last_name: "Smith",
                position: "Procurement Manager",
                email: "john.smith@techcorp.com",
                phone: "+1-555-0101"
              }
            ],
            segments: ["enterprise", "technology"],
            interests: ["bulk_electronics", "software_licensing"]
          },
          {
            id: 2,
            company_name: "GlobalWholesale Inc",
            business_type: "wholesaler",
            industry: "Consumer Goods",
            annual_revenue: "2500000.00",
            employee_count: 150,
            status: {
              id: 2,
              name: "Active",
              color_code: "#28a745"
            },
            credit_limit: "75000.00",
            payment_terms: "net_15",
            address: "456 Commerce Ave, New York, NY 10001",
            phone: "+1-555-GLOB-002",
            email: "orders@globalwholesale.com",
            website: "https://globalwholesale.com",
            registration_date: "2024-02-20",
            last_order_date: "2024-06-15",
            total_orders: 2,
            total_order_value: "185000.00",
            primary_contacts: [
              {
                id: 2,
                first_name: "Sarah",
                last_name: "Johnson",
                position: "Purchasing Director",
                email: "sarah@globalwholesale.com",
                phone: "+1-555-0202"
              }
            ],
            segments: ["mid_market", "consumer_goods"],
            interests: ["home_appliances", "kitchenware"]
          },
          {
            id: 3,
            company_name: "Metro Retail Chain",
            business_type: "retailer",
            industry: "Retail",
            annual_revenue: "1200000.00",
            employee_count: 75,
            status: {
              id: 1,
              name: "Lead",
              color_code: "#ffc107"
            },
            credit_limit: "50000.00",
            payment_terms: "net_30",
            address: "789 Market St, Chicago, IL 60601",
            phone: "+1-555-METRO-03",
            email: "procurement@metroretail.com",
            website: "https://metroretail.com",
            registration_date: "2024-03-10",
            last_order_date: null,
            total_orders: 0,
            total_order_value: "0.00",
            primary_contacts: [
              {
                id: 3,
                first_name: "Michael",
                last_name: "Chen",
                position: "Store Manager",
                email: "m.chen@metroretail.com",
                phone: "+1-555-0303"
              }
            ],
            segments: ["small_business", "retail"],
            interests: ["fashion", "accessories"]
          },
          {
            id: 4,
            company_name: "Premium Electronics Ltd",
            business_type: "distributor",
            industry: "Electronics",
            annual_revenue: "8000000.00",
            employee_count: 400,
            status: {
              id: 3,
              name: "VIP",
              color_code: "#dc3545"
            },
            credit_limit: "200000.00",
            payment_terms: "net_45",
            address: "321 Innovation Blvd, Austin, TX 78701",
            phone: "+1-555-PREM-004",
            email: "contact@premiumelec.com",
            website: "https://premiumelec.com",
            registration_date: "2023-11-05",
            last_order_date: "2024-06-25",
            total_orders: 8,
            total_order_value: "450000.00",
            primary_contacts: [
              {
                id: 4,
                first_name: "Jennifer",
                last_name: "Williams",
                position: "VP Procurement",
                email: "j.williams@premiumelec.com",
                phone: "+1-555-0404"
              }
            ],
            segments: ["enterprise", "electronics"],
            interests: ["consumer_electronics", "smart_devices"]
          },
          {
            id: 5,
            company_name: "Coastal Trading Co",
            business_type: "wholesaler",
            industry: "Import/Export",
            annual_revenue: "3500000.00",
            employee_count: 200,
            status: {
              id: 2,
              name: "Active",
              color_code: "#28a745"
            },
            credit_limit: "120000.00",
            payment_terms: "net_30",
            address: "555 Harbor View, Miami, FL 33101",
            phone: "+1-555-COAST-05",
            email: "info@coastaltrading.com",
            website: "https://coastaltrading.com",
            registration_date: "2024-01-30",
            last_order_date: "2024-05-18",
            total_orders: 4,
            total_order_value: "320000.00",
            primary_contacts: [
              {
                id: 5,
                first_name: "Roberto",
                last_name: "Martinez",
                position: "Operations Manager",
                email: "r.martinez@coastaltrading.com",
                phone: "+1-555-0505"
              }
            ],
            segments: ["mid_market", "import_export"],
            interests: ["international_goods", "seasonal_products"]
          },
          {
            id: 6,
            company_name: "Urban Boutique Network",
            business_type: "retailer",
            industry: "Fashion",
            annual_revenue: "900000.00",
            employee_count: 45,
            status: {
              id: 2,
              name: "Active",
              color_code: "#28a745"
            },
            credit_limit: "35000.00",
            payment_terms: "net_15",
            address: "888 Fashion Ave, Los Angeles, CA 90210",
            phone: "+1-555-URBAN-06",
            email: "buyers@urbanboutique.com",
            website: "https://urbanboutique.com",
            registration_date: "2024-04-12",
            last_order_date: "2024-06-22",
            total_orders: 6,
            total_order_value: "95000.00",
            primary_contacts: [
              {
                id: 6,
                first_name: "Isabella",
                last_name: "Rodriguez",
                position: "Lead Buyer",
                email: "i.rodriguez@urbanboutique.com",
                phone: "+1-555-0606"
              }
            ],
            segments: ["small_business", "fashion"],
            interests: ["trendy_apparel", "accessories"]
          }
        ];

        // Apply filters
        let filteredCustomers = allCustomers;

        if (search) {
          filteredCustomers = filteredCustomers.filter(customer =>
            customer.company_name.toLowerCase().includes(search.toLowerCase()) ||
            customer.industry.toLowerCase().includes(search.toLowerCase()) ||
            customer.business_type.toLowerCase().includes(search.toLowerCase())
          );
        }

        if (status) {
          filteredCustomers = filteredCustomers.filter(customer =>
            customer.status.name.toLowerCase() === status.toLowerCase()
          );
        }

        if (business_type) {
          filteredCustomers = filteredCustomers.filter(customer =>
            customer.business_type === business_type
          );
        }

        // Apply pagination
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedCustomers = filteredCustomers.slice(startIndex, endIndex);

        return {
          success: true,
          data: {
            customers: paginatedCustomers,
            pagination: {
              page: page,
              limit: limit,
              total: filteredCustomers.length,
              pages: Math.ceil(filteredCustomers.length / limit),
              has_next: endIndex < filteredCustomers.length,
              has_prev: page > 1
            },
            filters: {
              search: search,
              status: status,
              business_type: business_type
            }
          },
          // Legacy format for backward compatibility
          customers: paginatedCustomers,
          summary: {
            total_customers: filteredCustomers.length,
            active_customers: filteredCustomers.filter(c => c.status.name === "Active").length,
            total_revenue: filteredCustomers.reduce((sum, c) => sum + parseFloat(c.total_order_value), 0).toFixed(2),
            average_order_value: filteredCustomers.length > 0 ?
              (filteredCustomers.reduce((sum, c) => sum + parseFloat(c.total_order_value), 0) /
                filteredCustomers.reduce((sum, c) => sum + c.total_orders, 0)).toFixed(2) : "0.00"
          },
          count: filteredCustomers.length
        };
      }

      if (url.includes('/api/core/offline-crm/analytics/')) {
        return {
          success: true,
          data: {
            overview: {
              total_customers: 6,
              active_customers: 4,
              total_revenue: "1325000.00",
              average_ltv: "220833.33",
              total_orders: 23,
              average_order_value: "57608.70",
              growth_rate: "15.2",
              churn_rate: "3.1"
            },
            customer_status_distribution: [
              { status: "Active", count: 4, percentage: 66.7 },
              { status: "Lead", count: 1, percentage: 16.7 },
              { status: "VIP", count: 1, percentage: 16.7 }
            ],
            business_type_distribution: [
              { type: "distributor", count: 2, revenue: "725000.00" },
              { type: "wholesaler", count: 2, revenue: "505000.00" },
              { type: "retailer", count: 2, revenue: "95000.00" }
            ],
            monthly_revenue: [
              { month: "2023-11", revenue: "180000.00", orders: 3 },
              { month: "2023-12", revenue: "220000.00", orders: 4 },
              { month: "2024-01", revenue: "165000.00", orders: 2 },
              { month: "2024-02", revenue: "195000.00", orders: 3 },
              { month: "2024-03", revenue: "140000.00", orders: 2 },
              { month: "2024-04", revenue: "175000.00", orders: 3 },
              { month: "2024-05", revenue: "125000.00", orders: 2 },
              { month: "2024-06", revenue: "125000.00", orders: 4 }
            ],
            top_customers: [
              {
                company_name: "Premium Electronics Ltd",
                total_orders: 8,
                total_value: "450000.00",
                last_order: "2024-06-25"
              },
              {
                company_name: "Coastal Trading Co",
                total_orders: 4,
                total_value: "320000.00",
                last_order: "2024-05-18"
              },
              {
                company_name: "TechCorp Solutions",
                total_orders: 3,
                total_value: "275000.00",
                last_order: "2024-06-20"
              },
              {
                company_name: "GlobalWholesale Inc",
                total_orders: 2,
                total_value: "185000.00",
                last_order: "2024-06-15"
              },
              {
                company_name: "Urban Boutique Network",
                total_orders: 6,
                total_value: "95000.00",
                last_order: "2024-06-22"
              }
            ],
            payment_status_summary: {
              paid: { count: 18, amount: "1150000.00" },
              pending: { count: 3, amount: "125000.00" },
              overdue: { count: 2, amount: "50000.00" }
            },
            industry_breakdown: [
              { industry: "Technology", customers: 1, revenue: "275000.00" },
              { industry: "Electronics", customers: 1, revenue: "450000.00" },
              { industry: "Consumer Goods", customers: 1, revenue: "185000.00" },
              { industry: "Import/Export", customers: 1, revenue: "320000.00" },
              { industry: "Retail", customers: 1, revenue: "0.00" },
              { industry: "Fashion", customers: 1, revenue: "95000.00" }
            ],
            customer_acquisition: {
              this_month: 2,
              last_month: 1,
              growth_percentage: 100.0
            }
          },
          // Legacy format for backward compatibility
          analytics: {
            overview: {
              total_customers: 6,
              active_customers: 4,
              total_revenue: "1325000.00",
              average_ltv: "220833.33",
              total_orders: 23,
              average_order_value: "57608.70"
            },
            customer_status_distribution: [
              { status: "Active", count: 4, percentage: 66.7 },
              { status: "Lead", count: 1, percentage: 16.7 },
              { status: "VIP", count: 1, percentage: 16.7 }
            ],
            business_type_distribution: [
              { type: "distributor", count: 2, revenue: "725000.00" },
              { type: "wholesaler", count: 2, revenue: "505000.00" },
              { type: "retailer", count: 2, revenue: "95000.00" }
            ],
            monthly_revenue: [
              { month: "2023-11", revenue: "180000.00", orders: 3 },
              { month: "2023-12", revenue: "220000.00", orders: 4 },
              { month: "2024-01", revenue: "165000.00", orders: 2 },
              { month: "2024-02", revenue: "195000.00", orders: 3 },
              { month: "2024-03", revenue: "140000.00", orders: 2 },
              { month: "2024-04", revenue: "175000.00", orders: 3 },
              { month: "2024-05", revenue: "125000.00", orders: 2 },
              { month: "2024-06", revenue: "125000.00", orders: 4 }
            ],
            top_customers: [
              {
                company_name: "Premium Electronics Ltd",
                total_orders: 8,
                total_value: "450000.00",
                last_order: "2024-06-25"
              },
              {
                company_name: "Coastal Trading Co",
                total_orders: 4,
                total_value: "320000.00",
                last_order: "2024-05-18"
              },
              {
                company_name: "TechCorp Solutions",
                total_orders: 3,
                total_value: "275000.00",
                last_order: "2024-06-20"
              },
              {
                company_name: "GlobalWholesale Inc",
                total_orders: 2,
                total_value: "185000.00",
                last_order: "2024-06-15"
              }
            ],
            payment_status_summary: {
              paid: { count: 18, amount: "1150000.00" },
              pending: { count: 3, amount: "125000.00" },
              overdue: { count: 2, amount: "50000.00" }
            }
          }
        };
      }
    }
  }

  // Simulate a GET /brands endpoint
  if (method === 'GET' && url === '/brands') {
    // Simulated brand data
    return Promise.resolve([
      {
        name: 'Acme Corp',
        subscriptionTier: 'Gold',
        skuLimit: 1000,
        contractStart: '2024-01-01',
        contractEnd: '2025-01-01',
        renewalReminder: '2025-01-01',
        contact: { email: 'contact@acme.com', phone: '+1234567890' },
        supportHistory: [
          { ticketId: 'T001', date: '2024-03-01', status: 'Closed' },
          { ticketId: 'T002', date: '2024-04-15', status: 'Open' },
          { ticketId: 'T003', date: '2024-05-10', status: 'Closed' }
        ],
        onboardingTasks: [
          { task: 'Upload logo', dueDate: '2024-01-10', completed: true },
          { task: 'Add first product', dueDate: '2024-01-15', completed: false },
          { task: 'Verify bank details', dueDate: '2024-01-20', completed: true }
        ],
        campaignParticipation: ['Spring Sale', 'Summer Launch'],
        engagementLevel: 'High',
        tags: ['Top Seller', 'Eco Friendly'],
        segments: ['Fashion', 'Sustainable'],
        commissionSettlements: [
          { month: 'Jan', amount: 1200 },
          { month: 'Feb', amount: 1500 },
          { month: 'Mar', amount: 1100 }
        ]
      }
    ]);
  }

  // Mock customers endpoint for local development
  if (method === 'GET' && url === '/customers') {
    console.log('apiRequest: returning mocked customers data');
    return Promise.resolve(customersCRM);
  }
  if (method === 'GET' && url.startsWith('/calendar')) {
    // Parse query params
    const query = url.split('?')[1] || '';
    const params = new URLSearchParams(query);
    const year = params.get('year');
    const month = params.get('month'); // 1-12
    let filtered = calendarEvents;
    if (year && month) {
      const mm = month.padStart(2, '0');
      filtered = calendarEvents.filter((evt) => evt.date.startsWith(`${year}-${mm}-`));
    }
    return filtered;
  }
  throw new Error('API endpoint not implemented');
}

// Shipturtle Orders API
// Note: Backend must handle the 'search' parameter to filter orders by:
// - order_number
// - shopify_order_id  
// - customer name, email, phone
// If backend doesn't implement search, it will be ignored and all matching orders returned
export async function fetchShipturtleOrders(status = 'new', limit = 50, offset = 0, search = null, advancedFilters = null, sort = null) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('status', status);
    if (limit !== undefined && limit !== null) {
      queryParams.append('limit', String(limit));
    }
    if (offset !== undefined && offset !== null) {
      queryParams.append('offset', String(offset));
    }
    if (search !== null && search !== undefined && search !== '') {
      queryParams.append('search', String(search));
    }

    // Add sorting if provided
    if (sort) {
      if (sort.field) queryParams.append('sort_field', sort.field);
      if (sort.direction) queryParams.append('sort_direction', sort.direction);
    }

    // Add advanced filters if provided
    if (advancedFilters) {
      // Order number filter
      if (advancedFilters.orderNumber) {
        queryParams.append('order_number', advancedFilters.orderNumber);
      }
      // Carrier/shipper filter
      if (advancedFilters.carrierId) {
        queryParams.append('carrier_ids', advancedFilters.carrierId);
      }
      if (advancedFilters.carrierIds && advancedFilters.carrierIds.length > 0) {
        advancedFilters.carrierIds.forEach(id => queryParams.append('carrier_ids', id));
      }
      // Vendor filter
      if (advancedFilters.vendorIds && advancedFilters.vendorIds.length > 0) {
        advancedFilters.vendorIds.forEach(id => queryParams.append('vendor_ids', id));
      }
      // Invoice generated filter
      if (advancedFilters.invoiceGenerated !== undefined && advancedFilters.invoiceGenerated !== 'all') {
        queryParams.append('invoice_generated', advancedFilters.invoiceGenerated);
      }
      // Label generated filter
      if (advancedFilters.labelGenerated !== undefined && advancedFilters.labelGenerated !== 'all') {
        queryParams.append('label_generated', advancedFilters.labelGenerated);
      }
      // Shipment status filter
      if (advancedFilters.shipmentStatus && advancedFilters.shipmentStatus !== 'all') {
        queryParams.append('shipment_status', advancedFilters.shipmentStatus);
      }
      // Payment status filter
      if (advancedFilters.paymentStatus && advancedFilters.paymentStatus !== 'all') {
        queryParams.append('payment_status', advancedFilters.paymentStatus);
      }
      // Tags filter
      if (advancedFilters.tags && advancedFilters.tags.length > 0) {
        advancedFilters.tags.forEach(tag => queryParams.append('tags', tag));
      }
      // Customer filter
      if (advancedFilters.customer) {
        queryParams.append('customer', advancedFilters.customer);
      }
      // AWB filter
      if (advancedFilters.awb) {
        queryParams.append('awb', advancedFilters.awb);
      }
      // Date range filters
      if (advancedFilters.dateFrom) {
        queryParams.append('date_from', advancedFilters.dateFrom);
      }
      if (advancedFilters.dateTo) {
        queryParams.append('date_to', advancedFilters.dateTo);
      }
      // Delivered at date range
      if (advancedFilters.deliveredFrom) {
        queryParams.append('delivered_from', advancedFilters.deliveredFrom);
      }
      if (advancedFilters.deliveredTo) {
        queryParams.append('delivered_to', advancedFilters.deliveredTo);
      }
      // RTO sub-status filter (multi-select of reason codes)
      if (advancedFilters.rtoSubStatuses && advancedFilters.rtoSubStatuses.length > 0) {
        advancedFilters.rtoSubStatuses.forEach(code => queryParams.append('rto_sub_status', code));
      }
    }

    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      throw statusError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching Shipturtle orders:', error);
    throw describeFailure(error);
  }
}

// Fetch order filter options (carriers, statuses, vendors, etc.)
export async function fetchOrderFilterOptions() {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/filter-options/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw statusError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching order filter options:', error);
    throw describeFailure(error);
  }
}

// Fetch Order Counts API
export async function fetchOrderCounts(vendorId = null) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    const queryParams = vendorId ? `?vendor_id=${vendorId}` : '';
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/counts/${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      throw statusError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching order counts:', error);
    throw describeFailure(error);
  }
}

// Change Order Status API
export async function changeOrderStatus(orderId, status, notes = '') {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/status/`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        status: status,
        notes: notes
      })
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      } else if (response.status === 400) {
        // Business-rule rejections (e.g. error_code "shipment_required") are a
        // result, not a transport failure — return them so callers can react to
        // the specific code instead of a generic "HTTP 400".
        const errorData = await response.json().catch(() => ({}));
        return { success: false, ...errorData };
      }
      throw statusError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error changing order status:', error);
    throw describeFailure(error);
  }
}

// Bulk Status Update API
// Supports two modes:
// 1. orderIds array mode: when selectAll=false, uses orderIds array
// 2. select_all mode: when selectAll=true, uses filters (ignores orderIds)
export async function bulkUpdateShipturtleOrderStatus(orderIds, selectAll = false, filters = {}, status, notes = '') {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  // Build request body based on selectAll flag
  let requestBody;
  if (selectAll) {
    // Select all mode: use filters, ignore orderIds
    requestBody = {
      select_all: true,
      filters: filters,
      status: status,
      notes: notes
    };
  } else {
    // Order IDs mode: use orderIds array
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      throw new Error('No orders provided for bulk update');
    }
    requestBody = {
      order_ids: orderIds,
      status: status,
      notes: notes
    };
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/bulk-status-update/`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error in bulk status update:', error);
    throw describeFailure(error);
  }
}

// Generate Shipping Label API
// For PROZO, backend expects `carrier_name: 'PROZO'` AND a `channel_name` like "dtdc-surface".
// `channelName` is optional so existing callers (if any) don't break.
export async function generateShippingLabel(orderId, carrierName, channelName, itemBreadth, itemLength, itemHeight, itemWeight, shipperCompany) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/generate-label/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        carrier_name: carrierName,
        ...(channelName ? { channel_name: channelName } : {}),
        item_breadth: itemBreadth,
        item_length: itemLength,
        item_height: itemHeight,
        item_weight: itemWeight,
        ...(shipperCompany ? { shipper_company: shipperCompany } : {})
      })
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      if (response.status === 401) {
        return {
          success: false,
          error: 'Authentication failed - invalid or expired token',
          error_source: 'SERVER',
          detail: 'Your session may have expired. Please log in again.'
        };
      } else if (response.status === 403) {
        return {
          success: false,
          error: 'Access forbidden - insufficient permissions',
          error_source: 'SERVER',
          detail: 'You do not have permission to generate shipping labels.'
        };
      }
      // Return the error response from backend (includes error_source)
      return {
        success: false,
        error: data.error || `HTTP ${response.status}: ${response.statusText}`,
        error_source: data.error_source || 'SERVER',
        detail: data.detail || data.message || '',
        error_code: data.error_code || null,
        validation_errors: data.validation_errors || []
      };
    }

    return data;
  } catch (error) {
    console.error('[SERVER ERROR] Error generating shipping label:', error);
    return {
      success: false,
      error: error.message || 'Network error while generating label',
      error_source: 'SERVER',
      detail: 'Failed to connect to the server. Please check your internet connection.'
    };
  }
}

// Generate Invoices API (single or bulk)
// Accepts a list of order IDs and generates invoices where possible.
// Callers MUST always pass an array of order IDs (even for a single order).
export async function generateInvoices(orderIds) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/invoices/generate/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        order_ids: orderIds,
      }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error generating invoices:', error);
    throw describeFailure(error);
  }
}


// Get Order Invoice API
// Returns the latest active invoice metadata, including a direct download URL.
export async function getOrderInvoice(orderId) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/invoice/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      } else if (response.status === 404) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Invoice not found for this order');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching order invoice:', error);
    throw describeFailure(error);
  }
}

// Cancel Invoices API (single or bulk)
// Accepts a list of order IDs and cancels active invoices where possible.
export async function cancelInvoices(orderIds) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/invoices/cancel/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        order_ids: orderIds,
      }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      throw new Error(data.error || data.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return data;
  } catch (error) {
    console.error('Error cancelling invoices:', error);
    throw describeFailure(error);
  }
}

// Remove Shipping Label from Databae only API
export async function removeShippingLabel(orderId) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/remove-shipping-label/`, {
      method: "POST",
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': `application/json`
      },
      body: JSON.stringify({
        orderId: orderId,
      })
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - Invalid or Expired Token')
      }
      else if (response.status === 403) {
        throw new Error('Access Failed - Insufficient permissions');
      }
      const errorData = await response.json().catch(() => { });
      throw new Error(errorData.message || `HTTP ${response.status} : ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error removing Shipping Label");
    throw describeFailure(error);
  }
}

// Cancel Shipping Label API
// Supports two modes:
// 1. orderIds array mode: when selectAll=false, uses orderIds array
// 2. select_all mode: when selectAll=true, uses filters (ignores orderIds)
export async function cancelShippingLabel(orderIds, selectAll = false, filters = {}) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  // Build request body based on selectAll flag
  let requestBody;
  if (selectAll) {
    // Select all mode: use filters, ignore orderIds
    requestBody = {
      select_all: true,
      filters: filters
    };
  } else {
    // Order IDs mode: use orderIds array
    if (!Array.isArray(orderIds) || orderIds.length === 0) {
      throw new Error('No orders provided for label cancellation');
    }
    requestBody = {
      orderIds: orderIds
    };
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/cancel-shipping-label/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error cancelling shipping label:', error);
    throw describeFailure(error);
  }
}

// Download Combined Documents (Label + Manifest) API
// Merge one specific shipment's label + manifest. The order-scoped variant
// above resolves the order's *active* shipment, which a reverse or superseded
// leg never is — exception labels address their shipment directly.
export async function downloadShipmentCombinedDocuments(shipmentId) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  const response = await apiFetch(
    `${baseUrl}/api/shipturtle/orders/shipments/${shipmentId}/download-combined/`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication failed - invalid or expired token');
    }
    if (response.status === 403) {
      throw new Error('Access forbidden - insufficient permissions');
    }
    // A 404 is a semantic result, not a transport failure: the backend says
    // which document is missing. Surface that message rather than throwing.
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 404) {
      return { success: false, message: errorData.message || 'Label or manifest not found' };
    }
    throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

export async function downloadCombinedDocuments(orderId) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/download-combined/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      } else if (response.status === 404) {
        // A 404 here is a semantic result, not a transport failure: the backend
        // says exactly which document is missing (e.g. "Manifest not available
        // for this order"). Return it so callers can surface that message
        // instead of a generic error.
        const errorData = await response.json().catch(() => ({}));
        return { success: false, message: errorData.message || 'Label or manifest not found' };
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error downloading combined documents:', error);
    throw describeFailure(error);
  }
}

/**
 * Generate (or fetch an already-generated) manifest for a single order.
 *
 * Idempotent on the backend: if a manifest already exists it is returned as-is
 * without calling Prozo. Used as the manual retry when Prozo's post-label
 * manifest attempts have been exhausted.
 */
export async function generateOrderManifest(orderId) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/generate-manifest/`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({})
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    return { success: false, message: data.error || data.detail || 'Failed to generate manifest' };
  }
  return data;
}

// Email Order to Vendor API (single or bulk via orderIds / select_all + filters)
export async function emailOrderToVendor(orderIdOrIds, selectAll = false, filters = {}) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  const orderIds = Array.isArray(orderIdOrIds) ? orderIdOrIds : (orderIdOrIds ? [orderIdOrIds] : []);

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/email-vendor/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        order_ids: orderIds,
        select_all: selectAll,
        filters: selectAll ? filters : {}
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return data;
  } catch (error) {
    console.error('Error emailing order to vendor:', error);
    throw describeFailure(error);
  }
}

// Email Invoice to Customer API (single or bulk via orderIds / select_all + filters)
export async function emailInvoiceToCustomer(orderIdOrIds, selectAll = false, filters = {}) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  const orderIds = Array.isArray(orderIdOrIds) ? orderIdOrIds : (orderIdOrIds ? [orderIdOrIds] : []);

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/email-invoice/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        order_ids: orderIds,
        select_all: selectAll,
        filters: selectAll ? filters : {}
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || data.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    return data;
  } catch (error) {
    console.error('Error emailing invoice to customer:', error);
    throw describeFailure(error);
  }
}

export async function bulkGenerateManifestLabel(orderIds, selectAll = false, filters = {}, channelName = '', orderDimensions = {}) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  const body = selectAll 
    ? { select_all: true, filters, carrier_name: 'PROZO', channel_name: channelName }
    : { order_ids: orderIds, carrier_name: 'PROZO', channel_name: channelName };

  if (orderDimensions && Object.keys(orderDimensions).length > 0) {
    body.order_dimensions = orderDimensions;
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/bulk-generate-manifest-label/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      return { 
        success: false, 
        status: response.status,
        data 
      };
    }

    return data;
  } catch (error) {
    console.error('Error in bulk manifest & label generation:', error);
    throw describeFailure(error);
  }
}

// Helper function to download a file from a URL
export async function downloadFileFromUrl(url, filename, options = {}) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  try {
    // Handle relative URLs by prepending base URL
    let fullUrl = url;
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      // Remove leading slash if baseUrl already has trailing slash or vice versa
      const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const path = url.startsWith('/') ? url : `/${url}`;
      fullUrl = `${base}${path}`;
    }

    // Check if URL is an external URL (S3, CDN, etc.) or our backend
    const isExternalUrl = fullUrl.startsWith('http://') || fullUrl.startsWith('https://');
    const isOurBackend = isExternalUrl && fullUrl.includes(baseUrl);
    // Check if it's an S3 URL (S3 URLs don't accept Bearer tokens)
    const isS3Url = isExternalUrl && (fullUrl.includes('s3.') || fullUrl.includes('amazonaws.com'));

    // Only add authentication headers for our backend URLs, not for external URLs (S3, CDN, etc.)
    // S3 URLs are typically public or presigned, so they don't need/accept Bearer tokens
    const headers = {};
    if (token && !isS3Url && (isOurBackend || !isExternalUrl)) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await apiFetch(fullUrl, {
      method: 'GET',
      headers: headers,
      ...options
    });

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
    }

    // Get the blob
    const blob = await response.blob();

    // Create a blob URL
    const blobUrl = window.URL.createObjectURL(blob);

    // Create a temporary anchor element
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || 'download';
    link.style.display = 'none';

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL after a short delay
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
  } catch (error) {
    console.error('Error downloading file:', error);
    // Fallback: open in new tab if download fails
    const fallbackUrl = url && !url.startsWith('http://') && !url.startsWith('https://')
      ? `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`
      : url;
    window.open(fallbackUrl, '_blank', 'noopener');
    throw describeFailure(error);
  }
}

// Fetch Single Order Detail API
export async function fetchShipturtleOrderDetail(orderId) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      throw statusError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching order detail:', error);
    throw describeFailure(error);
  }
}

// Export order audit logs to Excel (single order)
export async function exportOrderLogs(orderId, orderNumber) {
  const url = `/api/shipturtle/orders/logs/export/?order_id=${orderId}`;
  const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
  const filename = orderNumber ? `order_logs_${orderNumber}_${dateStr}.xlsx` : `order_logs_${dateStr}.xlsx`;
  return downloadFileFromUrl(url, filename);
}

// Export order audit logs to Excel (all orders - bulk export)
export async function exportAdminOrderLogs() {
  const url = `/api/shipturtle/orders/logs/export/`;
  const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
  const filename = `all_order_logs_${dateStr}.xlsx`;
  return downloadFileFromUrl(url, filename);
}

/** Triggers shipping escalation: server builds the Excel attachment and sends the email. */
export async function escalateShippingOrders(escalationType) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/escalate/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        escalation_type: escalationType
      })
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      throw statusError(response);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error escalating shipping orders:', error);
    throw describeFailure(error);
  }
}

// Export product audit logs to Excel (single product)
export async function exportProductLogs(productId, productTitle) {
  const url = `/api/shipturtle/v2/products/logs/export/?product_id=${productId}`;
  const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
  const safeTitle = productTitle ? productTitle.replace(/[^a-zA-Z0-9-_ ]/g, '').substring(0, 50) : 'product';
  const filename = `product_logs_${safeTitle}_${dateStr}.xlsx`;
  return downloadFileFromUrl(url, filename);
}

// Export product audit logs to Excel (all products - bulk export)
export async function exportAdminProductLogs() {
  const url = `/api/shipturtle/v2/products/logs/export/`;
  const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
  const filename = `all_product_logs_${dateStr}.xlsx`;
  return downloadFileFromUrl(url, filename);
}

// Fetch vendor's pickup addresses by vendor_id
export async function fetchVendorPickupAddresses(vendorId) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  if (!vendorId) {
    throw new Error('Vendor ID is required');
  }

  try {
    // Build query parameters if is_default filter is needed (optional)
    const queryParams = new URLSearchParams();
    // Note: Backend supports optional ?is_default=true/false query param

    const url = `${baseUrl}/api/shipturtle/vendors/${vendorId}/pickup-addresses${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await apiFetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      } else if (response.status === 404) {
        throw new Error('Vendor not found');
      }
      throw statusError(response);
    }

    const data = await response.json();

    // Backend returns array directly, but component expects { pickup_addresses: [...] }
    const pickupAddresses = Array.isArray(data) ? data : [];

    return {
      pickup_addresses: pickupAddresses
    };
  } catch (error) {
    console.error('Error fetching vendor pickup addresses:', error);
    throw describeFailure(error);
  }
}

// Update pickup address of an order
export async function updateOrderPickupAddress(orderId, pickupAddressId) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  if (!orderId) {
    throw new Error('orderId is required to update pickup address');
  }

  if (!pickupAddressId) {
    throw new Error('pickupAddressId is required to update pickup address');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/pickup-address/`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        pickup_address_id: pickupAddressId
      })
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating order pickup address:', error);
    throw describeFailure(error);
  }
}

// Update shipping address of an order
// PUT /api/shipturtle/orders/<order_id>/shipping-address/
export async function updateOrderShippingAddress(orderId, payload) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  if (!orderId) {
    throw new Error('orderId is required to update shipping address');
  }

  if (!payload || typeof payload !== 'object') {
    throw new Error('payload is required to update shipping address');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/shipping-address/`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating order shipping address:', error);
    throw describeFailure(error);
  }
}

/**
 * Normalizes POST /api/shipturtle/orders/shipping-cost-calculator/ response.
 * Server returns serviceable courier quotes in `serviceable_options` (aligned with Prozo serviceability).
 * Falls back to legacy plain-array responses for older backends.
 *
 * Quotes now span both SURFACE and AIR. Each row carries a top-level `dispatch_mode`
 * ("SURFACE" | "AIR") and `provider.service_type_primary` (+ `provider.remarks`), used by
 * the UI to group by mode and resolve the bookable channel. `meta` also exposes
 * `dispatch_modes_requested` and `pricing_quote_count_by_mode`.
 *
 * @param {object|array} raw - Parsed JSON body
 * @returns {{ costs: array, meta: object|null, success: boolean, raw: * }}
 */
export function normalizeShippingCostCalculatorResponse(raw) {
  if (raw == null) {
    return { costs: [], meta: null, success: false, raw };
  }
  if (Array.isArray(raw)) {
    return { costs: raw, meta: null, success: true, raw };
  }
  if (typeof raw !== 'object') {
    return { costs: [], meta: null, success: false, raw };
  }
  const opts = raw.serviceable_options ?? raw.serviceable_partner_pricing;
  const costs = Array.isArray(opts) ? opts : [];
  return {
    costs,
    meta: raw.meta ?? null,
    success: raw.success !== false,
    raw,
  };
}

// Calculate Shipping Cost API (Dynamic)
export async function calculateShippingCost(payload) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/shipping-cost-calculator/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      const errorData = await response.json().catch(() => ({}));
      const message =
        errorData.message ||
        errorData.error ||
        errorData.detail ||
        `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(message);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error calculating shipping cost:', error);
    throw describeFailure(error);
  }
}


export async function addManualShippingDetails(orderId, payload) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  if (!orderId) {
    throw new Error('Order ID is required');
  }

  if (!payload || !payload.pickup_address_id || !payload.awb_number || !payload.shipping_partner) {
    throw new Error('Required fields: pickup_address_id, awb_number, shipping_partner');
  }

  try {
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/manual-shipping/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error adding manual shipping details:', error);
    throw describeFailure(error);
  }
}

// Bulk Payment Status Update API
// Unified endpoint for single and bulk updates
/**
 * Refactored: Unified Payment Status Update API
 * Uses endpoint: /api/shipturtle/orders/payment-status-update/
 * Handles both bulk and single order updates.

 */
export async function updateBulkPaymentStatus(orderIds, status, syncShopify = false, notes = '', selectAll = false, filters = {}) {
  const token = localStorage.getItem('token');
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!token) {
    throw new Error('Authentication token not found');
  }

  // Using 'paid' for all prepaid/paid actions and 'pending' for COD to avoid invalid status errors.
  const ACTION_TO_STATUS_MAP = {
    'paid': 'paid',
    'cod': 'pending',
    'prepaid': 'paid',
    'mark_as_paid': 'paid',
    'convert_to_cod': 'pending',
    'convert_to_prepaid': 'paid',
    'refunded': 'refunded',
    'mark_as_refunded': 'refunded'
  };

  const financialStatus = ACTION_TO_STATUS_MAP[status];

  if (!financialStatus) {
    throw new Error(`Invalid status action: ${status}`);
  }

  try {
    // Always send both parameters - backend will prioritize select_all if true
    const payload = {
      order_ids: orderIds || [],
      select_all: selectAll,
      filters: filters,
      financial_status: financialStatus,
      sync_shopify: syncShopify,
      notes: notes
    };

    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/payment-status-update/`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - invalid or expired token');
      } else if (response.status === 403) {
        throw new Error('Access forbidden - insufficient permissions');
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating payment status:', error);
    throw describeFailure(error);
  }
}

export async function updateTrackingLink(orderId, trackingLink) {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    if (!token) throw new Error('Authentication token not found');
    if (!orderId) throw new Error('Order ID is required');

    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/tracking-link/`, {
        method: 'PATCH',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tracking_link: trackingLink }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
}

export async function sendOrderFollowUp(orderId, stage, note = '') {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!token) throw new Error('Authentication token not found');
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/follow-up/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage, note }),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json();
}

export async function fetchOrderFollowUpState(orderId) {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!token) throw new Error('Authentication token not found');
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/`, {
        headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) return null;
    const data = await response.json();
    const o = data.order;
    if (!o) return null;
    return {
        followUpStage: o.follow_up_stage || 'none',
        firstFollowupSent: o.first_followup_sent || false,
        secondFollowupSent: o.second_followup_sent || false,
        thirdFollowupSent: o.third_followup_sent || false,
        penaltySent: o.penalty_sent || false,
        penaltyPercentage: o.penalty_percentage || 0,
        exceptionActive: o.exception_active || false,
        exceptionNote: o.exception_note || '',
    };
}

export async function setOrderException(orderId, note) {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!token) throw new Error('Authentication token not found');
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/exception/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json();
}

export async function revokeOrderException(orderId) {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!token) throw new Error('Authentication token not found');
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/revoke-exception/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json();
}

export async function applyOrderPenalty(orderId) {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!token) throw new Error('Authentication token not found');
    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/penalty/`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
    }
    return response.json();
}

export async function sendAddressVerificationEmail(orderId) {
    const token = localStorage.getItem('token');
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    if (!token) throw new Error('Authentication token not found');
    if (!orderId) throw new Error('Order ID is required');

    const response = await apiFetch(`${baseUrl}/api/shipturtle/orders/${orderId}/send-address-verification/`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
        },
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
}
